import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { GraduationCap, BookOpen, TrendingUp, PlayCircle, Sparkles } from "lucide-react";

const ICONS = { TrendingUp, BookOpen, PlayCircle };
const DEFAULT_ACCENT = "from-nexus-pink to-nexus-purple";

function toneForTag(tag) {
  switch ((tag || "").toLowerCase()) {
    case "growth": return "text-nexus-pink";
    case "business": return "text-nexus-blue";
    case "craft": return "text-nexus-purple";
    default: return "text-white";
  }
}

/**
 * Public read-only badge strip for a creator.
 *
 * Props:
 *   userId  — creator's user id (required)
 *   compact — small chip row (default) vs. large detail cards
 *   maxRows — cap how many badges to show (default 5)
 *   title   — optional heading text; omit to render the bare row
 */
export default function AcademyBadges({ userId, compact = true, maxRows = 5, title = null }) {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!userId) { setBadges([]); setLoading(false); return; }
    (async () => {
      try {
        const r = await api.get(`/academy/badges/${userId}`);
        if (!cancelled) setBadges(r?.data?.badges || []);
      } catch {
        if (!cancelled) setBadges([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading || badges.length === 0) return null;

  const visible = badges.slice(0, maxRows);
  const extra = badges.length - visible.length;

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-testid="academy-badges-compact">
        {title && <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1 flex items-center gap-1">
          <GraduationCap className="h-3 w-3" /> {title}
        </span>}
        {visible.map((b) => {
          const Icon = ICONS[b.module_icon] || BookOpen;
          return (
            <Link
              key={b.module_id}
              to={`/academy/${b.module_id}`}
              className="group inline-flex items-center gap-1.5 h-6 px-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 hover:bg-emerald-500/15 transition-colors"
              title={`Academy graduate — ${b.module_title}`}
              data-testid={`academy-badge-${b.module_id}`}
            >
              <Icon className={`h-3 w-3 ${toneForTag(b.module_tag)}`} />
              <span className="text-[10px] uppercase tracking-widest text-emerald-200 font-bold truncate max-w-[120px]">
                {b.module_tag || "Academy"}
              </span>
            </Link>
          );
        })}
        {extra > 0 && (
          <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-white/10 text-zinc-400 text-[10px] uppercase tracking-widest">
            +{extra}
          </span>
        )}
      </div>
    );
  }

  // Detail cards (used on the public profile)
  return (
    <section className="space-y-3" data-testid="academy-badges-detail">
      {title && (
        <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
          <GraduationCap className="h-3.5 w-3.5 text-nexus-pink" /> {title}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
        {visible.map((b) => {
          const Icon = ICONS[b.module_icon] || BookOpen;
          const accent = b.module_accent || DEFAULT_ACCENT;
          return (
            <Link
              key={b.module_id}
              to={`/academy/${b.module_id}`}
              className="glass rounded-xl p-3 hover:border-nexus-pink/40 transition-colors flex items-start gap-2.5 group"
              data-testid={`academy-badge-card-${b.module_id}`}
            >
              <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center shrink-0`}>
                <Icon className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1">
                  <Sparkles className="h-2.5 w-2.5" /> Academy graduate
                </div>
                <div className="text-sm font-semibold text-white truncate">{b.module_title}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
